import * as THREE from 'three';

function _rotateXYPoint(pt: THREE.Vector3, angle: number): THREE.Vector3 {
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  return new THREE.Vector3(cos * pt.x - sin * pt.y, sin * pt.x + cos * pt.y, pt.z);
}

export function pointCloud(
  layers: Array<[number, number]>,
  numPts: number,
  startingLayerIdx = 0
): Array<Array<THREE.Vector3>> {
  const rotAngle = (Math.PI * 2) / numPts;
  const ret = new Array<Array<THREE.Vector3>>(layers.length - startingLayerIdx);
  for (let i = startingLayerIdx, sz = layers.length; i < sz; ++i) {
    const l = layers[i];
    const initialPt = new THREE.Vector3(l[0], 0, l[1]);
    const pt = _rotateXYPoint(initialPt, i % 2 == 0 ? 0 : rotAngle / 2);
    const ptsLayer = new Array<THREE.Vector3>(numPts);
    for (let j = 0; j < numPts; ++j) {
      ptsLayer[j] = _rotateXYPoint(pt, rotAngle * j);
    }
    ret[i - startingLayerIdx] = ptsLayer;
  }
  return ret;
}

export function wireframe(ptsCloud: Array<Array<THREE.Vector3>>, startingLayerIdx = 0): Array<Array<THREE.Line3>> {
  const ret = new Array<Array<THREE.Line3>>(ptsCloud.length - 1);
  for (let i = 0, sz = ptsCloud.length - 1; i < sz; ++i) {
    const layer1 = ptsCloud[i];
    const layer2 = ptsCloud[i + 1];
    const lnsLayer = new Array<THREE.Line3>(layer1.length * 2);
    for (let j = 0; j < layer1.length; ++j) {
      let k = 0;
      if (i % 2 == startingLayerIdx % 2) {
        k = j != 0 ? j - 1 : layer1.length - 1;
      } else {
        k = j != layer1.length - 1 ? j + 1 : 0;
      }
      lnsLayer[j * 2] = new THREE.Line3(layer1[j], layer2[j]);
      lnsLayer[j * 2 + 1] = new THREE.Line3(layer1[j], layer2[k]);
    }
    ret[i] = lnsLayer;
  }
  return ret;
}

export function triangles(
  ptsCloud: Array<Array<THREE.Vector3>>,
  startingLayerIdx = 0
): Array<Array<[THREE.Vector3, THREE.Vector3, THREE.Vector3]>> {
  const ret = new Array<Array<[THREE.Vector3, THREE.Vector3, THREE.Vector3]>>(ptsCloud.length - 1);
  for (let i = 0, sz = ptsCloud.length - 1; i < sz; ++i) {
    const layer1 = ptsCloud[i];
    const layer2 = ptsCloud[i + 1];
    const triLayer = new Array<[THREE.Vector3, THREE.Vector3, THREE.Vector3]>(layer1.length * 2);
    for (let j = 0; j < layer1.length; ++j) {
      const k = j != 0 ? j - 1 : layer1.length - 1;
      const l = j != layer1.length - 1 ? j + 1 : 0;
      if (i % 2 == startingLayerIdx % 2) {
        triLayer[j * 2] = [layer1[j], layer2[j], layer2[k]];
        triLayer[j * 2 + 1] = [layer1[j], layer1[l], layer2[j]];
      } else {
        triLayer[j * 2] = [layer1[j], layer2[j], layer1[k]];
        triLayer[j * 2 + 1] = [layer1[j], layer2[l], layer2[j]];
      }
      ret[i] = triLayer;
    }
  }
  return ret;
}

export function rhombuses(
  ptsCloud: Array<Array<THREE.Vector3>>,
  startingLayerIdx = 0
): Array<Array<[THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3]>> {
  const ret = new Array<Array<[THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3]>>(ptsCloud.length - 2);
  for (let i = 1, sz = ptsCloud.length - 1; i < sz; ++i) {
    const layer0 = ptsCloud[i - 1];
    const layer1 = ptsCloud[i];
    const layer2 = ptsCloud[i + 1];
    const rhmLayer = new Array<[THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3]>(layer1.length);
    for (let j = 0; j < layer1.length; ++j) {
      if (i % 2 == startingLayerIdx % 2) {
        const k = j != layer1.length - 1 ? j + 1 : 0;
        rhmLayer[j] = [layer0[j], layer1[k], layer2[j], layer1[j]];
      } else {
        const k = j != 0 ? j - 1 : layer1.length - 1;
        rhmLayer[j] = [layer0[j], layer1[j], layer2[j], layer1[k]];
      }
    }
  }
  return ret;
}
